// frontend/src/components/ManageUserModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-18
// Updated:      2026-09-24
//
// Description:
//   Modal for managing user profiles, including safe self-service profile editing,
//   resource-linked home address/postcode, avatar management and password changes.
//
// 2026-09-24:
//   • Adds explicit self-profile mode using the authenticated user from AuthContext.
//   • Loads profile data from /users/{id}/profile or /users/me/profile.
//   • Self-profile updates never submit role/admin/status/activation fields.
//   • Resource-linked home address/postcode are shown only when a Resource exists.
//   • Adds current-password verified password change action for self-service users.
//   • Adds visible but disabled 2FA control for future implementation.
//   • If the current user changes username, local auth is cleared after save because
//     existing JWT tokens currently use username as the token subject.
//   • Avatar edit control reduced to a transparent pencil-only action with no circular
//     background or border; upload behaviour is unchanged.
//   • Loads avatars directly from the stored /static URL instead of authenticated
//     Axios/blob previews, allowing normal browser caching on desktop and mobile.
//   • Cache-busts the avatar URL only after a successful avatar upload.

import React, { useEffect, useRef, useState } from "react";

import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import ModalWrapper from "./ModalWrapper";

const emptyPasswordForm = {
  current_password: "",
  new_password: "",
  confirm_password: "",
};

const ManageUserModal = ({ userId, onClose }) => {
  const { user: currentUser, logout } = useAuth();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(null);
  const [originalUsername, setOriginalUsername] = useState("");
  const [avatarVersion, setAvatarVersion] = useState(0);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const isSelf = Number(userId) === Number(currentUser?.id);

  const loadUser = async () => {
    try {
      const endpoint = isSelf ? "/users/me/profile" : `/users/${userId}/profile`;
      const res = await axiosInstance.get(endpoint);
      setForm(res.data);
      setOriginalUsername(res.data?.username || "");
      setAvatarLoadFailed(false);
    } catch (err) {
      console.error("Failed to load user profile:", err.response?.data || err);
      alert(err.response?.data?.detail || "Failed to load user profile.");
      onClose?.();
    }
  };

  useEffect(() => {
    setAvatarVersion(0);
    setAvatarLoadFailed(false);
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, currentUser?.id]);

  const avatarSrc = (() => {
    if (!form?.avatar_url) return null;
    if (!avatarVersion) return form.avatar_url;

    const separator = form.avatar_url.includes("?") ? "&" : "?";
    return `${form.avatar_url}${separator}v=${avatarVersion}`;
  })();

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;

    const payload = new FormData();
    payload.append("file", file);

    try {
      await axiosInstance.post(`/users/${userId}/avatar`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await loadUser();
      setAvatarLoadFailed(false);
      setAvatarVersion(Date.now());
    } catch (err) {
      console.error("Avatar upload failed:", err.response?.data || err);
      alert(err.response?.data?.detail || "Avatar upload failed.");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUpdate = async () => {
    if (!form) return;

    const profilePayload = {
      first_name: form.first_name || null,
      last_name: form.last_name || null,
      username: form.username || "",
      email: form.email || "",
    };

    if (form.has_resource) {
      profilePayload.home_address = form.home_address || null;
      profilePayload.home_postcode = form.home_postcode || null;
    }

    setSaving(true);
    try {
      const profileEndpoint = isSelf
        ? "/users/me/profile"
        : `/users/${userId}/profile`;

      const profileRes = await axiosInstance.patch(profileEndpoint, profilePayload);

      if (!isSelf) {
        await axiosInstance.put(`/users/${userId}`, {
          role: form.role || "User",
        });
      }

      const usernameChanged =
        isSelf &&
        originalUsername &&
        profileRes.data?.username &&
        profileRes.data.username !== originalUsername;

      setForm(profileRes.data);
      setOriginalUsername(profileRes.data?.username || "");

      if (usernameChanged) {
        alert("Profile updated. Because your username changed, please sign in again.");
        await logout();
        return;
      }

      alert("User updated successfully.");
      await loadUser();
      onClose?.();
    } catch (err) {
      console.error("User update failed:", err.response?.data || err);
      alert(err.response?.data?.detail || "Failed to update user.");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (!isSelf) return;

    if (!passwordForm.current_password) {
      alert("Enter your current password.");
      return;
    }

    if (passwordForm.new_password.length < 8) {
      alert("New password must be at least 8 characters.");
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert("New password and confirmation do not match.");
      return;
    }

    setPasswordSaving(true);
    try {
      await axiosInstance.post("/users/me/change-password", passwordForm);
      setPasswordForm(emptyPasswordForm);
      alert("Password updated successfully.");
    } catch (err) {
      console.error("Password update failed:", err.response?.data || err);
      alert(err.response?.data?.detail || "Failed to update password.");
    } finally {
      setPasswordSaving(false);
    }
  };

  if (!form) return null;

  return (
    <ModalWrapper onClose={onClose}>
      <div className="mb-6 flex min-w-0 flex-col items-center gap-3 text-center sm:flex-row sm:items-center sm:gap-4 sm:text-left">
        <div className="relative h-20 w-20 shrink-0">
          {avatarSrc && !avatarLoadFailed ? (
            <img
              src={avatarSrc}
              alt="Avatar"
              loading="eager"
              decoding="async"
              onError={() => setAvatarLoadFailed(true)}
              className="h-full w-full rounded-full border object-cover"
            />
          ) : (
            <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center rounded-full bg-gray-300 text-xl font-bold text-white">
              {form.first_name?.[0]}
              {form.last_name?.[0]}
            </div>
          )}

          <button
            type="button"
            onClick={handleAvatarClick}
            className="absolute bottom-0 right-0 flex h-6 w-6 items-center justify-center bg-transparent p-0 text-base leading-none transition-opacity hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-btnActionNormal"
            title="Change avatar"
            aria-label="Change avatar"
          >
            ✏️
          </button>

          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        <div className="min-w-0 flex-1">
          <p className="break-words text-lg font-semibold">
            {form.first_name} {form.last_name}
          </p>
          <p className="break-all text-sm text-gray-500">{form.email}</p>
          {isSelf && (
            <p className="mt-1 text-xs text-gray-400">Your profile</p>
          )}
        </div>
      </div>

      <section className="min-w-0 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
          Profile
        </h3>

        <input
          type="text"
          value={form.first_name || ""}
          onChange={(e) => handleChange("first_name", e.target.value)}
          placeholder="First Name"
          className="w-full min-w-0 rounded border p-2"
        />

        <input
          type="text"
          value={form.last_name || ""}
          onChange={(e) => handleChange("last_name", e.target.value)}
          placeholder="Last Name"
          className="w-full min-w-0 rounded border p-2"
        />

        <input
          type="text"
          value={form.username || ""}
          onChange={(e) => handleChange("username", e.target.value)}
          placeholder="Username"
          className="w-full min-w-0 rounded border p-2"
        />

        <input
          type="email"
          value={form.email || ""}
          onChange={(e) => handleChange("email", e.target.value)}
          placeholder="Email"
          className="w-full min-w-0 rounded border p-2"
        />

        {!isSelf && (
          <select
            value={form.role || ""}
            onChange={(e) => handleChange("role", e.target.value)}
            className="w-full min-w-0 rounded border p-2"
          >
            <option value="">Select Role</option>
            <option>Super Admin</option>
            <option>System Admin</option>
            <option>System Manager</option>
            <option>Operator</option>
            <option>User</option>
            <option>Client</option>
            <option>Guest</option>
          </select>
        )}
      </section>

      {form.has_resource && (
        <section className="mt-6 min-w-0 space-y-4 border-t pt-5">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
              Resource details
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              Optional personal details linked to the Resource record.
            </p>
          </div>

          <textarea
            value={form.home_address || ""}
            onChange={(e) => handleChange("home_address", e.target.value)}
            placeholder="Home Address"
            rows={3}
            className="w-full min-w-0 resize-y rounded border p-2"
          />

          <input
            type="text"
            value={form.home_postcode || ""}
            onChange={(e) => handleChange("home_postcode", e.target.value)}
            placeholder="Home Postcode"
            className="w-full min-w-0 rounded border p-2"
          />
        </section>
      )}

      {isSelf && (
        <section className="mt-6 min-w-0 space-y-4 border-t pt-5">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
              Security
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              Change your password using your current password for verification.
            </p>
          </div>

          <input
            type="password"
            autoComplete="current-password"
            value={passwordForm.current_password}
            onChange={(e) =>
              handlePasswordChange("current_password", e.target.value)
            }
            placeholder="Current Password"
            className="w-full min-w-0 rounded border p-2"
          />

          <input
            type="password"
            autoComplete="new-password"
            value={passwordForm.new_password}
            onChange={(e) => handlePasswordChange("new_password", e.target.value)}
            placeholder="New Password"
            className="w-full min-w-0 rounded border p-2"
          />

          <input
            type="password"
            autoComplete="new-password"
            value={passwordForm.confirm_password}
            onChange={(e) =>
              handlePasswordChange("confirm_password", e.target.value)
            }
            placeholder="Confirm New Password"
            className="w-full min-w-0 rounded border p-2"
          />

          <button
            type="button"
            onClick={handlePasswordUpdate}
            disabled={passwordSaving}
            className="w-full rounded border border-gray-300 bg-white px-4 py-2 font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {passwordSaving ? "Updating Password..." : "Change Password"}
          </button>

          <div className="flex items-center justify-between gap-4 rounded border bg-gray-50 p-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-700">
                Two-factor authentication
              </div>
              <div className="text-xs text-gray-500">Coming soon</div>
            </div>
            <button
              type="button"
              disabled
              className="shrink-0 rounded border bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-500 opacity-70"
            >
              Enable 2FA
            </button>
          </div>
        </section>
      )}

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={handleUpdate}
          disabled={saving}
          className="w-full rounded bg-btnActionNormal px-4 py-2 font-semibold text-white shadow transition-colors duration-200 hover:bg-btnActionHover active:bg-btnActionClick disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {saving ? "Updating..." : isSelf ? "Update Profile" : "Update User"}
        </button>
      </div>
    </ModalWrapper>
  );
};

export default ManageUserModal;
